import type {
  PhaseBlockConfig,
  PhaseBlock,
  QuoteBlock,
  PhaseBlockConfigPhase,
  PhaseBlockConfigPrice,
  PhaseBlockConfigDiscount,
  PhaseBlockConfigMinimum,
  ListBlockConfig,
  ListBlockConfigV2,
  ListBlockConfigV3,
  EnrichedText,
  TextBlockConfig
} from '@sequencehq/quote-content'
import { OrangeOrange60 } from '@sequencehq/core-components'
import { objectsAreEqualIgnoreUndefined } from '@sequencehq/utils'
import type { PresentationV3 } from 'Cube/domain/cube.domain.types'
import { cubeDeveloperLogGroup } from 'modules/Cube/utils/cubeDeveloperLog'
import { QUOTE_SYNC_LOG_LABEL } from 'modules/Cube/view/layouts/quote/sync/quoteSyncDeveloperLog'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { match } from 'ts-pattern'
import { getPresentationChanges } from './presentationChanges'

export function hasPresentationChanged(
  prev: PresentationV3,
  current: PresentationV3
): boolean {
  const prevComparablePresentation = toComparable(prev)
  const currentComparablePresentation = toComparable(current)
  const presentationHasChanged = !objectsAreEqualIgnoreUndefined(
    prevComparablePresentation,
    currentComparablePresentation
  )

  if (presentationHasChanged) {
    cubeDeveloperLogGroup({
      label: QUOTE_SYNC_LOG_LABEL,
      summary: 'Comparing presentations - changed',
      badgeBackgroundColor: OrangeOrange60,
      entries: [
        {
          label: 'Changes',
          value: () =>
            getPresentationChanges(
              prevComparablePresentation,
              currentComparablePresentation
            )
        }
      ]
    })
  }

  return presentationHasChanged
}

export function toComparable({ blocks, ...presentation }: PresentationV3) {
  return {
    ...presentation,
    blocks: blocks.flatMap((block): Array<Record<any, any>> => {
      if (isPhaseBlock(block)) {
        return [
          {
            ...block,
            config: convertPhaseBlockToComparableEntity(block.config)
          }
        ]
      }

      /**
       * Because of the grouping changes in v3 of the list block configs,
       * we need to 'ungroup' them for comparison.
       */
      if (isBulletListBlock(block) || isNumberListBlock(block)) {
        return ungroupListBlock(block)
      }

      if (isTextBlock(block)) {
        return [withoutJustifiedText(block)]
      }

      return [block]
    }),
    // Drop metadata from the comparison: metadata (totalContractValue,
    // totalDuration, etc.) is derived from other fields by the out adapter, so
    // a recomputed metadata diff should not flag the quote as having unsaved
    // presentation changes. Pairs with `applyMetadataToPresentation` skipping
    // metadata recomputation for accepted quotes.
    metadata: undefined
  }
}

/**
 * Lists used to keep every item in one block, but the editor saves one block per
 * item, so we split them the same way before comparing. Item text, colours and
 * alignment are filled in the way the editor saves them too, so an older list
 * doesn't look changed just because the editor loaded it.
 */
export const ungroupListBlock = (block: QuoteBlock): QuoteBlock[] =>
  match(block.version)
    .with(3, () => ungroupListBlockV3(block))
    .with(2, () =>
      (block.config as ListBlockConfigV2).items.map(item => ({
        ...block,
        config: { items: [toEnrichedText(item)] }
      }))
    )
    .otherwise(() =>
      (block.config as ListBlockConfig).items.map(item => ({
        ...block,
        config: { items: [item] }
      }))
    )

const ungroupListBlockV3 = (block: QuoteBlock): QuoteBlock[] => {
  const config = block.config as ListBlockConfigV3
  const isNumberList = isNumberListBlock(block)

  if (config.items.length > 1) {
    return config.items.map((item, idx) => ({
      ...block,
      config: {
        items: [withListItemDefaults(item)],
        children: [],
        ...(isNumberList && { startsFrom: (config.startsFrom ?? 1) + idx })
      }
    }))
  }

  /**
   * Bullet lists drop table children when loading into the editor, so those
   * never count as a change.
   */
  const children = (config.children ?? [])
    .filter(child => isNumberList || (child && child.blockType !== 'table'))
    .flatMap(child =>
      isBulletListBlock(child) || isNumberListBlock(child)
        ? ungroupListBlock(child)
        : [child]
    )

  return [
    {
      ...block,
      config: {
        items: config.items.map(withListItemDefaults),
        children,
        ...(isNumberList && { startsFrom: config.startsFrom })
      }
    }
  ]
}

/**
 * The editor has no justify, so justified text loads left aligned. That alone
 * isn't a change, so it's only saved alongside a real edit.
 */
const withoutJustifiedText = (block: QuoteBlock): QuoteBlock => {
  const config = block.config as TextBlockConfig

  return config.textAlignment === 'justify'
    ? { ...block, config: { ...config, textAlignment: 'left' } }
    : block
}

const withListItemDefaults = (item: ListBlockConfigV3['items'][number]) => ({
  text: toEnrichedText(item.text),
  backgroundColor: item.backgroundColor ?? 'default',
  textColor: item.textColor ?? 'default',
  textAlignment: item.textAlignment ?? 'left'
})

const toEnrichedText = (
  text: string | (string | null)[] | EnrichedText[]
): EnrichedText[] => {
  if (typeof text === 'string') {
    return [{ text, styles: {} }]
  }

  if (text.length === 0) {
    return [{ text: '', styles: {} }]
  }

  if (typeof text[0] === 'string') {
    return (text as (string | null)[]).map(item => ({
      text: item ?? '',
      styles: {}
    }))
  }

  return (text as EnrichedText[]).map(item => ({
    text: item.text,
    href: item.href,
    variable: item.variable,
    styles: pick(item.styles, [
      'bold',
      'italic',
      'underline',
      'strike',
      'textColor',
      'backgroundColor'
    ])
  }))
}

/**
 * Just ... :emlofire:
 *
 * For anyone unfortunate enough to find this function - our API isn't suited to
 * syncing, and we have to strip ids. We also want to avoid array ordering impacting
 * the diff (as, as of the time of writing, ordering doesn't matter - it may do eventually,
 * though!). To do this, we need to give all of the entities consistent ids to reference as
 * well. That is the productId!
 * @param config
 * @returns
 */
export function convertPhaseBlockToComparableEntity(
  config: PhaseBlockConfig
): Omit<PhaseBlockConfig, 'phases'> & {
  phases: Array<{
    dates: PhaseBlockConfigPhase['dates']
    prices: Record<
      string,
      Omit<
        PhaseBlockConfigPrice,
        'id' | 'createdAt' | 'updatedAt' | 'integrationIds' | 'status'
      >
    >
    discounts: Record<string, Omit<PhaseBlockConfigDiscount, 'id' | 'priceIds'>>
    globalDiscount:
      | Omit<PhaseBlockConfigDiscount, 'id' | 'priceIds'>
      | undefined
    minimums: Record<
      string,
      Omit<PhaseBlockConfigMinimum, 'id' | 'scope.priceIds'>
    >
    name?: string
  }>
} {
  return {
    ...config,
    phases: config.phases.map(phase => {
      const { prices, discounts, minimums } = phase
      const globalDiscount =
        phase.globalDiscount ??
        discounts.find(discount => discount.applyToAllPrices)

      return {
        name: phase.name,
        dates: {
          ...phase.dates,
          /**
           * Zero the absent fields so equivalent durations compare equal. A sentinel has nothing
           * to zero, and flattening it would match a zero length phase.
           */
          duration:
            typeof phase.dates.duration === 'string'
              ? phase.dates.duration
              : {
                  years: phase.dates.duration?.years || 0,
                  months: phase.dates.duration?.months || 0,
                  days: phase.dates.duration?.days || 0
                }
        },
        prices: prices.reduce(
          (acc, price) => ({
            ...acc,
            [price.productId]: {
              ...omit(price, [
                'id',
                'createdAt',
                'updatedAt',
                'integrationIds',
                'status'
              ]),
              /**
               * The pricing editor fills in `overagesBillingFrequency` whenever the API
               * returns a seat-based price without one — the price's own billing frequency
               * when billed in advance, otherwise "NEVER". That default only exists client
               * side, so comparing it reports a change the user never made and the quote
               * never shows. `prorateFlatFees` is here for the same reason: it is only
               * ever set by the editor, never by anything the quote displays.
               */
              structure: omit(price.structure, [
                'overagesBillingFrequency',
                'prorateFlatFees'
              ])
            }
          }),
          {}
        ),
        discounts: discounts
          .filter(discount => !discount.applyToAllPrices)
          .reduce((acc, discount) => {
            const productsAssociatedWithDiscount = discount.priceIds
              .map(
                priceId => prices.find(price => price.id === priceId)?.productId
              )
              .sort()
              .filter(Boolean)

            return {
              ...acc,
              [productsAssociatedWithDiscount.join(',')]: omit(discount, [
                'id',
                'priceIds'
              ])
            }
          }, {}),
        globalDiscount: globalDiscount
          ? omit(globalDiscount, ['id', 'priceIds'])
          : undefined,
        minimums: minimums.reduce((acc, minimum) => {
          const productsAssociatedWithMinimum =
            minimum.scope.target === 'allUsage'
              ? ['all']
              : minimum.scope.priceIds
                  .map(
                    priceId =>
                      prices.find(price => price.id === priceId)?.productId
                  )
                  .sort()
                  .filter(Boolean)

          return {
            ...acc,
            [productsAssociatedWithMinimum.join(',')]: omit(minimum, [
              'id',
              'scope.priceIds'
            ])
          }
        }, {})
      }
    })
  }
}

export function isPhaseBlock(block: {
  blockType: QuoteBlock['blockType']
}): block is PhaseBlock {
  return block.blockType === 'phases'
}

export function isBulletListBlock(block: {
  blockType: QuoteBlock['blockType']
}): block is PhaseBlock {
  return block.blockType === 'bulletList'
}

export function isNumberListBlock(block: {
  blockType: QuoteBlock['blockType']
}): block is PhaseBlock {
  return block.blockType === 'numberList'
}

const TEXT_BLOCK_TYPES: QuoteBlock['blockType'][] = [
  'paragraph',
  'heading1',
  'heading2',
  'heading3'
]

const isTextBlock = (block: QuoteBlock) =>
  TEXT_BLOCK_TYPES.includes(block.blockType)

const NON_EDITOR_BLOCKTYPES = ['coverImage', 'pdfCoverImage']
export const mergeEditorPresentationIntoFullPresentation = (
  editorPresentation: QuoteBlock[],
  fullPresentation: PresentationV3
) => {
  // Right now coverImage (when it exists) is the first block so we can just concat
  // In the future we might need to handle this differently to account for ordering
  return {
    ...fullPresentation,
    blocks: fullPresentation.blocks
      .filter(block => NON_EDITOR_BLOCKTYPES.includes(block.blockType))
      .concat(editorPresentation)
  }
}
